import React, { useEffect, useMemo, useState } from "react";
import { Accordion, ActionDropdown, IconButton } from 'components/common';
import { FaPlus, FaEdit, FaTrashAlt } from "react-icons/fa";
import TinymceEditor from "components/common/TinymceEditor";
import { CRM_PIF_ROOF_PANEL_COLOR, CRM_PIF_RV_TYPE } from "app_config";
import Select from "react-select"
import BDSGeneralInformation from "./BDSGeneralInformation";
import { useSelector } from "react-redux";
import PropTypes from 'prop-types';
import { INPUT_CLS, SELECT_STYLES, CHECK_CLS, TH_CLS, TD_CLS, SECTION_BODY_CLS } from "../../pifStyles";

const CHECK_LABEL_CLS = "tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none";

const BDS2 = ({
  data,
  roofExtensionData,
  newRoofExtension,
  editRoofExtension,
  deleteRoofExtension,
  canopyData,
  newCanopy,
  editCanopy,
  deleteCanopy,
  fasciaData,
  newFascia,
  editFascia,
  deleteFascia,
  frameOpeningData,
  newFrameOpening,
  editFrameOpening,
  deleteFrameOpening,
  mezzanineData,
  newMezzanine,
  editMezzanine,
  deleteMezzanine,
  craneInformationData,
  newCraneInformation,
  editCraneInformation,
  deleteCraneInformation
}) => {
  const {bds2: initialKeys} = useSelector(state => state.crm_project_pif.initialKeys)
  const [activeKey, setActiveKey] = useState(initialKeys);

  const {area} = data
  const {control, register, Controller, setValue} = data.useHookForm

  const areaId = area.id
  const {bds2} = area

  useEffect(() => {
    setActiveKey(initialKeys);
  }, [initialKeys]);

  useEffect(() => {
    if (bds2) {
      Object.entries(bds2).forEach(([key, value]) => {
        setValue(`bds2_${key}_${area.id}`, value)
      })
    }
  }, [bds2, setValue, area.id]);

  const roofPaintColorOptions = useMemo(() => (
    CRM_PIF_ROOF_PANEL_COLOR.map((option) => ({value: option, label: option}))
  ), [])
  const defaultRoofPaintColorOption = bds2 ? { value: bds2.roof_monitor_panel_and_color, label: bds2.roof_monitor_panel_and_color } : null

  const rvTypeOptions = useMemo(() => (
    CRM_PIF_RV_TYPE.map((option) => ({value: option, label: option}))
  ), [])
  const defaultRvTypeOption = bds2 ? { value: bds2.ridge_vent_rv_type, label: bds2.ridge_vent_rv_type } : null

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  return (
    <Accordion
      activeKeys={activeKey}
      onChange={handleAccordionSelect}
      className="tw-text-sm"
    >
      <Accordion.Item eventKey="0" header="General Information">
        <div className={SECTION_BODY_CLS}>
          <BDSGeneralInformation data={data} />
          <h6 className="tw-text-center tw-my-2">This area only : Page 2 of 3</h6>
        </div>
      </Accordion.Item>

      {/* 12. Roof Extension */}
      <Accordion.Item eventKey="1" header="12. Roof Extension">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newRoofExtension(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Roof Extension</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>Location</th>
                  <th className={TH_CLS} rowSpan={2}>Qty</th>
                  <th className={TH_CLS} rowSpan={2}>Width (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Length (M)</th>
                  <th className={TH_CLS} colSpan={2}>Soffit panel</th>
                  <th className={TH_CLS} rowSpan={2}>Insulated</th>
                  <th className={TH_CLS} rowSpan={2}>Description</th>
                </tr>
                <tr className="tw-text-center">
                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                </tr>
              </thead>
              <tbody>
                {roofExtensionData && roofExtensionData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editRoofExtension(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteRoofExtension(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.location}</td>
                    <td className={TD_CLS}>{item.qty}</td>
                    <td className={TD_CLS}>{item.width}</td>
                    <td className={TD_CLS}>{item.length}</td>
                    <td className={TD_CLS}>{item.soffit_panel_by}</td>
                    <td className={TD_CLS}>{item.soffit_panel_description}</td>
                    <td className={TD_CLS}>{item.insulated}</td>
                    <td className={TD_CLS}>{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3">
            <Controller
              control={control}
              name={`bds2_roof_extension_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 13. Canopy */}
      <Accordion.Item eventKey="2" header="13. Canopy">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newCanopy(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Canopy</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>Location</th>
                  <th className={TH_CLS} rowSpan={2}>Qty</th>
                  <th className={TH_CLS} rowSpan={2}>Width (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Length (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Clear height (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Eave condition</th>
                  <th className={TH_CLS} rowSpan={2}>Slope</th>
                  <th className={TH_CLS} colSpan={3}>Roof panel</th>
                  <th className={TH_CLS} colSpan={3}>Endwall panel</th>
                  <th className={TH_CLS} colSpan={3}>Soffit panel</th>
                </tr>
                <tr className="tw-text-center">
                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>

                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>

                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>
                </tr>
              </thead>
              <tbody>
                {canopyData && canopyData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editCanopy(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteCanopy(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.location}</td>
                    <td className={TD_CLS}>{item.qty}</td>
                    <td className={TD_CLS}>{item.width}</td>
                    <td className={TD_CLS}>{item.length}</td>
                    <td className={TD_CLS}>{item.clear_height}</td>
                    <td className={TD_CLS}>{item.eave_condition}</td>
                    <td className={TD_CLS}>{item.slope}</td>

                    <td className={TD_CLS}>{item.roof_panel_by}</td>
                    <td className={TD_CLS}>{item.roof_panel_description}</td>
                    <td className={TD_CLS}>{item.roof_panel_color}</td>

                    <td className={TD_CLS}>{item.end_wall_panel_by}</td>
                    <td className={TD_CLS}>{item.end_wall_panel_description}</td>
                    <td className={TD_CLS}>{item.end_wall_panel_color}</td>

                    <td className={TD_CLS}>{item.soffit_panel_by}</td>
                    <td className={TD_CLS}>{item.soffit_panel_description}</td>
                    <td className={TD_CLS}>{item.soffit_panel_color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3">
            <Controller
              control={control}
              name={`bds2_capony_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 14. Fascia */}
      <Accordion.Item eventKey="3" header="14. Fascia">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newFascia(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Fascia</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>Location</th>
                  <th className={TH_CLS} rowSpan={2}>Qty</th>
                  <th className={TH_CLS} rowSpan={2}>Type</th>
                  <th className={TH_CLS} rowSpan={2}>Height (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Length (M)</th>
                  <th className={TH_CLS} rowSpan={2}>Proj. (M)</th>

                  <th className={TH_CLS} colSpan={3}>Fascia panel</th>
                  <th className={TH_CLS} colSpan={3}>Soffit panel</th>
                  <th className={TH_CLS} colSpan={3}>Backup panel</th>
                </tr>
                <tr className="tw-text-center">
                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>

                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>

                  <td className={TH_CLS}>By</td>
                  <td className={TH_CLS}>Description</td>
                  <td className={TH_CLS}>Color</td>
                </tr>
              </thead>
              <tbody>
                {fasciaData && fasciaData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editFascia(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteFascia(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.location}</td>
                    <td className={TD_CLS}>{item.qty}</td>
                    <td className={TD_CLS}>{item.fascia_type}</td>
                    <td className={TD_CLS}>{item.height}</td>
                    <td className={TD_CLS}>{item.length}</td>
                    <td className={TD_CLS}>{item.proj}</td>

                    <td className={TD_CLS}>{item.fasica_panel_by}</td>
                    <td className={TD_CLS}>{item.fasica_panel_description}</td>
                    <td className={TD_CLS}>{item.fasica_panel_color}</td>

                    <td className={TD_CLS}>{item.soffit_panel_by}</td>
                    <td className={TD_CLS}>{item.soffit_panel_description}</td>
                    <td className={TD_CLS}>{item.soffit_panel_color}</td>

                    <td className={TD_CLS}>{item.backup_panel_by}</td>
                    <td className={TD_CLS}>{item.backup_panel_description}</td>
                    <td className={TD_CLS}>{item.backup_panel_color}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3">
            <Controller
              control={control}
              name={`bds2_fascia_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 15. Frame Opening */}
      <Accordion.Item eventKey="4" header="15. Frame Opening">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newFrameOpening(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Frame Opening</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>Qty</th>
                  <th className={TH_CLS} rowSpan={2}>Width (mm)</th>
                  <th className={TH_CLS} rowSpan={2}>Height (mm)</th>
                  <th className={TH_CLS} rowSpan={2}>For</th>
                  <th className={TH_CLS} rowSpan={2}>Doors, window,…By</th>
                  <th className={TH_CLS} rowSpan={2}>Location</th>
                </tr>
              </thead>
              <tbody>
                {frameOpeningData && frameOpeningData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editFrameOpening(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteFrameOpening(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.qty}</td>
                    <td className={TD_CLS}>{item.width}</td>
                    <td className={TD_CLS}>{item.height}</td>
                    <td className={TD_CLS}>{item.frame_opening_for}</td>
                    <td className={TD_CLS}>{item.door_window_by}</td>
                    <td className={TD_CLS}>{item.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3">
            <Controller
              control={control}
              name={`bds2_frame_opening_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 16. Mezzanine */}
      <Accordion.Item eventKey="5" header="16. Mezzanine">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-3 tw-mb-2">
            <div className="tw-col-span-7">
              <div className="tw-flex tw-gap-x-2">
                <div>PEB to Supply :</div>
                <div className="tw-flex-1 tw-flex tw-justify-between">
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_peb_supply_cols_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Cols</span>
                      </label>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_peb_supply_beams_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Beams</span>
                      </label>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_peb_supply_joists_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Joists</span>
                      </label>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_peb_supply_deck_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Deck</span>
                      </label>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_peb_supply_handrails_mezz_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Handrails Mezz.</span>
                      </label>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2">
                <div>
                  <Controller
                    control={control}
                    name={`bds2_mezzinine_apply_load_only_${area.id}`}
                    render={({ field }) => (
                      <label className={CHECK_LABEL_CLS}>
                        <input
                          type="checkbox"
                          className={CHECK_CLS}
                          checked={!!field.value}
                          onChange={(value) => field.onChange(value)}
                        />
                        <span>Apply loads only</span>
                      </label>
                    )}
                  />
                </div>
                <div className="tw-flex-1">
                  <input
                    placeholder="Apply loads only"
                    className={INPUT_CLS}
                    {...register(`bds2_mezzinine_apply_load_only_specify_${area.id}`)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newMezzanine(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Mezzanine</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>Mz no</th>
                  <th className={TH_CLS} rowSpan={2}>DL (kN/m2)</th>
                  <th className={TH_CLS} rowSpan={2}>LL (kN/m2)</th>
                  <th className={TH_CLS} rowSpan={2}>Coll (kN/m2)</th>
                  <th className={TH_CLS} rowSpan={2}>Area (m2)</th>
                  <th className={TH_CLS} rowSpan={2}>Clearance (mm)</th>
                  <th className={TH_CLS} colSpan={2}>Floor</th>
                  <th className={TH_CLS} rowSpan={2}>Stair Type</th>
                  <th className={TH_CLS} rowSpan={2}>Qty</th>
                  <th className={TH_CLS} rowSpan={2}>Width (mm) (IN/IN ofStringer)</th>
                  <th className={TH_CLS} colSpan={2}>Landing</th>
                  <th className={TH_CLS} rowSpan={2}>Tread Type</th>
                  <th className={TH_CLS} rowSpan={2}>Hand Rail</th>
                </tr>
                <tr className="tw-text-center">
                  <th className={TH_CLS}>Type</th>
                  <th className={TH_CLS}>Total thickness (mm)</th>

                  <th className={TH_CLS}>Mid</th>
                  <th className={TH_CLS}>Top</th>
                </tr>
              </thead>
              <tbody>
                {mezzanineData && mezzanineData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editMezzanine(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteMezzanine(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.mz_no}</td>
                    <td className={TD_CLS}>{item.dl}</td>
                    <td className={TD_CLS}>{item.ll}</td>
                    <td className={TD_CLS}>{item.coll}</td>
                    <td className={TD_CLS}>{item.area}</td>
                    <td className={TD_CLS}>{item.clearance}</td>
                    <td className={TD_CLS}>{item.floor_type}</td>
                    <td className={TD_CLS}>{item.floor_t}</td>
                    <td className={TD_CLS}>{item.stair_type}</td>
                    <td className={TD_CLS}>{item.qty}</td>
                    <td className={TD_CLS}>{item.width}</td>
                    <td className={`${TD_CLS} tw-text-center`}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        name={`landing_mid_${item.id}`}
                        checked={item.landing_mid}
                        readOnly
                      />
                    </td>
                    <td className={`${TD_CLS} tw-text-center`}>
                      <input
                        type="checkbox"
                        className={CHECK_CLS}
                        name={`landing_top_${item.id}`}
                        checked={item.landing_top}
                        readOnly
                      />
                    </td>
                    <td className={TD_CLS}>{item.tread_type}</td>
                    <td className={TD_CLS}>{item.hand_rail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3 tw-mb-2">
            <Controller
              control={control}
              name={`bds2_mezzanine_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
          <div className="tw-flex tw-gap-x-3 tw-mb-2">
            <div className="tw-flex-1">
              <Controller
                control={control}
                name={`bds2_mezzinine_concrete_floor_thickness_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Is Concrete floor thickness included finished floor?</span>
                  </label>
                )}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                <div>Finish floor thickness (mm):</div>
                <div className="tw-flex-1">
                  <input
                    placeholder="Finish floor thickness (mm)"
                    className={INPUT_CLS}
                    {...register(`bds2_mezzinine_finish_floor_thickness_${area.id}`)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2 tw-items-center">
            <div>Edge of mezzanine slab is expanded to out of :</div>
            <div className="tw-flex-1">
              <input
                placeholder="Edge of mezzanine slab is expanded to out of"
                className={INPUT_CLS}
                {...register(`bds2_mezzanine_edge_slab_${area.id}`)}
              />
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 17. Roof Monitor */}
      <Accordion.Item eventKey="6" header="17. Roof Monitor">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-3 tw-mb-2">
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center">
              <div>Width at throat (mm):</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Width at throat (mm)"
                  className={INPUT_CLS}
                  {...register(`bds2_roof_monitor_width_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center">
              <div>Height (mm):</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Height (mm)"
                  className={INPUT_CLS}
                  {...register(`bds2_roof_monitor_height_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center">
              <div>Roof Slope:</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Roof Slope"
                  className={INPUT_CLS}
                  {...register(`bds2_roof_monitor_slope_${area.id}`)}
                />
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-3 tw-mb-2">
            <div className="tw-col-span-4 tw-flex tw-gap-x-2 tw-items-center">
              <div>Eave Condition:</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Eave Condition"
                  className={INPUT_CLS}
                  {...register(`bds2_roof_monitor_eave_condition_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-col-span-8 tw-flex tw-gap-x-2 tw-items-center">
              <div>Roof panel &amp; color:</div>
              <div className="tw-flex-1">
                <Controller
                  control={control}
                  name={`bds2_roof_monitor_panel_and_color_${area.id}`}
                  render={() => (
                    <Select
                      closeMenuOnSelect={true}
                      options={roofPaintColorOptions}
                      defaultValue={defaultRoofPaintColorOption}
                      placeholder='Select...'
                      styles={SELECT_STYLES}
                      isClearable
                      onChange={selectedOption => setValue(`bds2_roof_monitor_panel_and_color_${area.id}`, selectedOption ? selectedOption.value : null, {shouldValidate: true})}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-3 tw-items-center">
            <div>Wall Condition:</div>
            <div className="tw-flex-1">
              <input
                placeholder="Wall Condition"
                className={INPUT_CLS}
                {...register(`bds2_roof_monitor_wall_condition_${area.id}`)}
              />
            </div>
          </div>
          <div>
            <Controller
              control={control}
              name={`bds2_roof_monitor_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 18. Ridge Vent */}
      <Accordion.Item eventKey="7" header="18. Ridge Vent">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex tw-gap-x-2 tw-items-center">
              <div>RV Type:</div>
              <div className="tw-w-64">
                <Controller
                  control={control}
                  name={`bds2_ridge_vent_rv_type_${area.id}`}
                  render={() => (
                    <Select
                      closeMenuOnSelect={true}
                      options={rvTypeOptions}
                      defaultValue={defaultRvTypeOption}
                      placeholder='Select...'
                      styles={SELECT_STYLES}
                      isClearable
                      onChange={selectedOption => setValue(`bds2_ridge_vent_rv_type_${area.id}`, selectedOption ? selectedOption.value : null, {shouldValidate: true})}
                    />
                  )}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-mt-2 tw-ml-3">
              <Controller
                control={control}
                name={`bds2_ridge_vent_non_standard_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>NON-STANDARD</span>
                  </label>
                )}
              />
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-3">
            <div>
              <div className="tw-mb-1">Quantity:</div>
              <input
                placeholder="Quantity"
                className={INPUT_CLS}
                {...register(`bds2_ridge_vent_qty_${area.id}`)}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-mb-1">Length:</div>
              <input
                placeholder="Length"
                className={INPUT_CLS}
                {...register(`bds2_ridge_vent_length_${area.id}`)}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-mb-1">Roof panel:</div>
              <input
                placeholder="Roof panel"
                className={INPUT_CLS}
                {...register(`bds2_ridge_vent_roof_panel_${area.id}`)}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-mb-1">Color:</div>
              <input
                placeholder="Color"
                className={INPUT_CLS}
                {...register(`bds2_ridge_vent_color_${area.id}`)}
              />
            </div>
            <div className="tw-flex-1">
              <div className="tw-mb-1">Remark:</div>
              <input
                placeholder="Remark"
                className={INPUT_CLS}
                {...register(`bds2_ridge_vent_remark_${area.id}`)}
              />
            </div>
          </div>
          <div>
            <Controller
              control={control}
              name={`bds2_ridge_vent_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      {/* 19. Crane Information */}
      <Accordion.Item eventKey="8" header="19. Crane Information">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-3 tw-mb-2">
            <div>Scope of Supply:</div>
            <div className="tw-flex-1 tw-flex tw-justify-between">
              <Controller
                control={control}
                name={`bds2_crane_only_apply_loads_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Only Apply Loads,</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name={`bds2_crane_beams_brackets_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Crane Beams &amp; Brackets,</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name={`bds2_crane_rails_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Rails,</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name={`bds2_crane_system_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Crane system,</span>
                  </label>
                )}
              />
              <Controller
                control={control}
                name={`bds2_crane_bracket_only_${area.id}`}
                render={({ field }) => (
                  <label className={CHECK_LABEL_CLS}>
                    <input
                      type="checkbox"
                      className={CHECK_CLS}
                      checked={!!field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    <span>Bracket only,</span>
                  </label>
                )}
              />
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex tw-gap-x-2 tw-items-center tw-mb-3">
              <div>Power Supply. Volts:</div>
              <div>
                <input
                  placeholder="Power Supply. Volts "
                  className={INPUT_CLS}
                  {...register(`bds2_crane_power_supply_volt_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center tw-mb-3">
              <div>Hz:</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Hz"
                  className={INPUT_CLS}
                  {...register(`bds2_crane_hz_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center tw-mb-3">
              <div>Phases:</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Phases"
                  className={INPUT_CLS}
                  {...register(`bds2_crane_phase_${area.id}`)}
                />
              </div>
            </div>
            <div className="tw-flex-1 tw-flex tw-gap-x-2 tw-items-center tw-mb-3">
              <div>Manufacturer:</div>
              <div className="tw-flex-1">
                <input
                  placeholder="Manufacturer"
                  className={INPUT_CLS}
                  {...register(`bds2_crane_manufacturer_${area.id}`)}
                />
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-end">
            <IconButton
              size="sm"
              icon={<FaPlus size={12} />}
              className="tw-mr-1"
              onClick={() => newCraneInformation(areaId)}
            >
              <span className="tw-hidden xl:tw-inline-block tw-ml-1">New Crane Information</span>
            </IconButton>
          </div>

          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center">
                  <th className={TH_CLS} rowSpan={2}></th>
                  <th className={TH_CLS} rowSpan={2}>No.</th>
                  <th className={TH_CLS} rowSpan={2}>Crane Type</th>
                  <th className={TH_CLS} colSpan={3}>Span</th>
                  <th className={TH_CLS} colSpan={3}>Run length</th>
                  <th className={TH_CLS} rowSpan={2}>Capacity (M.T)</th>
                  <th className={TH_CLS} rowSpan={2}>Wheel Load (kN)</th>
                  <th className={TH_CLS} rowSpan={2}>Wheel Base (mm)</th>
                  <th className={TH_CLS} rowSpan={2}>Bumper (mm)</th>
                  <th className={TH_CLS} rowSpan={2}>Min. Hook Height</th>
                  <th className={TH_CLS} rowSpan={2}>Top of Crane Beam</th>
                  <th className={TH_CLS} rowSpan={2}>Bridge Weight (kg)</th>
                  <th className={TH_CLS} rowSpan={2}>Trolley Weight (kg)</th>
                  <th className={TH_CLS} colSpan={2}>Operaion</th>
                </tr>
                <tr className="tw-text-center">
                  <th className={TH_CLS}>W1 (m)</th>
                  <th className={TH_CLS}>From (GL)</th>
                  <th className={TH_CLS}>To (GL)</th>

                  <th className={TH_CLS}>(m)</th>
                  <th className={TH_CLS}>From (GL)</th>
                  <th className={TH_CLS}>To (GL)</th>

                  <th className={TH_CLS}>Pendant</th>
                  <th className={TH_CLS}>Cab</th>
                </tr>
              </thead>
              <tbody>
                {craneInformationData && craneInformationData.filter(item => item.area_id === area.id).map((item, index) => (
                  <tr key={index}>
                    <td className={TD_CLS}>
                      <ActionDropdown
                        align="end"
                        items={[
                          {
                            label: 'Edit',
                            icon: <FaEdit size={12} />,
                            onClick: () => editCraneInformation(areaId, item)
                          },
                          { divider: true },
                          {
                            label: 'Delete',
                            icon: <FaTrashAlt size={12} />,
                            onClick: () => deleteCraneInformation(areaId, item),
                            danger: true
                          }
                        ]}
                      />
                    </td>
                    <td className={TD_CLS}>{item.crane_sr}</td>
                    <td className={TD_CLS}>{item.crane_type}</td>
                    <td className={TD_CLS}>{item.span_w1}</td>
                    <td className={TD_CLS}>{item.span_from_gl}</td>
                    <td className={TD_CLS}>{item.span_to_gl}</td>
                    <td className={TD_CLS}>{item.run_length}</td>
                    <td className={TD_CLS}>{item.run_length_from_gl}</td>
                    <td className={TD_CLS}>{item.run_length_to_gl}</td>
                    <td className={TD_CLS}>{item.capacity}</td>
                    <td className={TD_CLS}>{item.wheel_load}</td>
                    <td className={TD_CLS}>{item.wheel_base}</td>
                    <td className={TD_CLS}>{item.bumper}</td>
                    <td className={TD_CLS}>{item.min_hook_height}</td>
                    <td className={TD_CLS}>{item.top_of_crane_beam}</td>
                    <td className={TD_CLS}>{item.bridge_weight}</td>
                    <td className={TD_CLS}>{item.trolley_weight}</td>

                    <td className={`${TD_CLS} tw-text-center`}>
                      <input type="checkbox" className={CHECK_CLS} checked={item.operation_pendant} readOnly />
                    </td>
                    <td className={`${TD_CLS} tw-text-center`}>
                      <input type="checkbox" className={CHECK_CLS} checked={item.operation_cab} readOnly />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3 tw-mb-2">
            <Controller
              control={control}
              name={`bds2_crane_information_note_${area.id}`}
              render={({ field }) => (
                <TinymceEditor rollback={false}
                  height="30vh"
                  handleChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          </div>
        </div>
      </Accordion.Item>

      <Accordion.Item eventKey="9" header="Attached">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-justify-between">
            <Controller
              control={control}
              name={`bds2_attached_for_this_building_${area.id}`}
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>For This Building/ Area Only,</span>
                </label>
              )}
            />
            <Controller
              control={control}
              name={`bds2_attached_bldg_data_sheet3_${area.id}`}
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>BLDG.Data Sheet 3,</span>
                </label>
              )}
            />
            <Controller
              control={control}
              name={`bds2_attached_bldg_data_sheet4_${area.id}`}
              render={({ field }) => (
                <label className={CHECK_LABEL_CLS}>
                  <input
                    type="checkbox"
                    className={CHECK_CLS}
                    checked={!!field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  <span>BLDG.Data Sheet 4,</span>
                </label>
              )}
            />
          </div>
        </div>
      </Accordion.Item>
    </Accordion>
  )
};

BDS2.propTypes = {
  data: PropTypes.object.isRequired,
  roofExtensionData: PropTypes.func.isRequired,
  newRoofExtension: PropTypes.func.isRequired,
  editRoofExtension: PropTypes.func.isRequired,
  deleteRoofExtension: PropTypes.func.isRequired,
  canopyData: PropTypes.func.isRequired,
  newCanopy: PropTypes.func.isRequired,
  editCanopy: PropTypes.func.isRequired,
  deleteCanopy: PropTypes.func.isRequired,
  fasciaData: PropTypes.func.isRequired,
  newFascia: PropTypes.func.isRequired,
  editFascia: PropTypes.func.isRequired,
  deleteFascia: PropTypes.func.isRequired,
  frameOpeningData: PropTypes.func.isRequired,
  newFrameOpening: PropTypes.func.isRequired,
  editFrameOpening: PropTypes.func.isRequired,
  deleteFrameOpening: PropTypes.func.isRequired,
  mezzanineData: PropTypes.func.isRequired,
  newMezzanine: PropTypes.func.isRequired,
  editMezzanine: PropTypes.func.isRequired,
  deleteMezzanine: PropTypes.func.isRequired,
  craneInformationData: PropTypes.func.isRequired,
  newCraneInformation: PropTypes.func.isRequired,
  editCraneInformation: PropTypes.func.isRequired,
  deleteCraneInformation: PropTypes.func.isRequired
};

export default BDS2;
